/*
 * at-graph.js — the pitch graph of /autotune: a piano roll with the sung
 * pitch, the tuned pitch and the notes, which can be moved.
 *
 *   const g = AutotuneGraph.create(canvas, {onSeek, onSelect, onMove, noteName});
 *   g.setData({hop, midi, out, notes, rowShift, pcs, duration});
 *   g.setCurve(out); g.setPlayhead(t); g.zoom(2); g.select(k);
 *
 * Gestures (one owner each): a tap is the browser's click — on a note it
 * selects the note, elsewhere it moves the playhead; a mouse drag that starts
 * vertically on a note moves the note by semitones; any other drag past the
 * threshold pans a zoomed view. On touch the page keeps its vertical scroll
 * (touch-action: pan-y) — notes move with the ▲▼ buttons or the arrow keys —
 * until the page turns editing on (setEditing(true): the canvas takes the
 * touch, touch-action: none), then a finger drags a note like the mouse.
 * The plain mouse wheel scrolls the page; Ctrl+wheel zooms.
 * ES2019.
 */
(function (root) {
  'use strict';

  // Theme colours come from the canvas's own ancestors: the design tokens
  // (--lg-*) live on the tool's container, not on :root, and differ between
  // the dark and the light theme.
  function css(el, name, fallback) {
    try {
      var v = getComputedStyle(el).getPropertyValue(name);
      return v && v.trim() ? v.trim() : fallback;
    } catch (e) { return fallback; }
  }
  // any CSS colour → the same colour at alpha a (the canvas normalises it)
  var probe = null;
  function alpha(color, a) {
    try {
      if (!probe) probe = document.createElement('canvas').getContext('2d');
      probe.fillStyle = '#000';
      probe.fillStyle = color;
      var c = probe.fillStyle;
      var r, g, b;
      if (c.charAt(0) === '#') { r = parseInt(c.substr(1, 2), 16); g = parseInt(c.substr(3, 2), 16); b = parseInt(c.substr(5, 2), 16); }
      else { var m = c.match(/[\d.]+/g); r = +m[0]; g = +m[1]; b = +m[2]; }
      return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
    } catch (e) { return 'rgba(128,128,128,' + a + ')'; }
  }

  function create(canvas, opts) {
    opts = opts || {};
    var d = null;            // data
    var view = { t0: 0, t1: 1 };
    var sel = -1;
    var playhead = null;
    var drag = null;
    var editing = false;
    var yLo = 40, yHi = 70;
    var GUTTER = 44, AXIS = 18;
    var dpr = 1;

    function size() {
      var r = canvas.getBoundingClientRect();
      dpr = Math.min(3, window.devicePixelRatio || 1);
      var w = Math.max(200, Math.round(r.width)), h = Math.max(120, Math.round(r.height));
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      return { w: w, h: h };
    }
    function tx(t, w) { return GUTTER + (t - view.t0) / (view.t1 - view.t0) * (w - GUTTER); }
    function xt(x, w) { return view.t0 + (x - GUTTER) / (w - GUTTER) * (view.t1 - view.t0); }
    function my(m, h) { return (h - AXIS) - (m - yLo) / (yHi - yLo) * (h - AXIS); }
    function ym(y, h) { return yLo + ((h - AXIS) - y) / (h - AXIS) * (yHi - yLo); }

    function setData(data) {
      d = data;
      // the range the voice lives in: the 2nd and 98th percentile of its
      // pitch, not the extremes — one stray frame an octave off made the
      // first view two octaves tall with the voice in its bottom third
      var vals = [];
      for (var i = 0; i < d.midi.length; i++) if (!isNaN(d.midi[i])) vals.push(d.midi[i]);
      vals.sort(function (a, b) { return a - b; });
      var lo = vals.length ? vals[Math.floor(vals.length * 0.02)] : 55;
      var hi = vals.length ? vals[Math.min(vals.length - 1, Math.floor(vals.length * 0.98))] : 67;
      var mid = (lo + hi) / 2, span = Math.max(12, hi - lo + 4);
      yLo = mid - span / 2; yHi = mid + span / 2;
      // A note has to be big enough to hit with a finger: at most ~60 px per
      // second of audio on the first view (a phone shows ~5 s, a desktop
      // ~15 s); a longer take scrolls, the playhead pulls the view along.
      var plotW = Math.max(160, size().w - GUTTER);
      view.t0 = 0; view.t1 = Math.min(Math.max(0.5, d.duration), Math.max(4, plotW / 60));
      sel = -1;
      redraw();
      if (opts.onView) opts.onView(view.t0, view.t1, Math.max(0.5, d.duration));
    }
    function setCurve(out) { if (d) { d.out = out; redraw(); } }
    function setNotes(notes, overrides) { if (d) { d.notes = notes; d.overrides = overrides || {}; if (sel >= notes.length) sel = -1; redraw(); } }
    // land: where the notes sound against the grid they are named on (A = 440
    // or the singer's own A) — the rows and the note bars move together
    function setRows(rowShift, pcs, land) { if (d) { d.rowShift = rowShift; d.pcs = pcs; if (land != null) d.land = land; redraw(); } }
    function setPlayhead(t) { playhead = t; redraw(); }

    function zoom(f, anchor) {
      if (!d) return;
      var span = view.t1 - view.t0;
      var full = Math.max(0.5, d.duration);
      var ns = Math.min(full, Math.max(1.5, span / f));
      var a = anchor != null ? anchor : (playhead != null && playhead >= view.t0 && playhead <= view.t1 ? playhead : (view.t0 + view.t1) / 2);
      var u = (a - view.t0) / span;
      view.t0 = a - u * ns;
      view.t1 = view.t0 + ns;
      clampView();
      redraw();
      if (opts.onView) opts.onView(view.t0, view.t1, full);
    }
    function setViewStart(t0) {
      if (!d) return;
      var span = view.t1 - view.t0;
      view.t0 = t0; view.t1 = t0 + span;
      clampView();
      redraw();
    }
    function clampView() {
      var full = Math.max(0.5, d.duration), span = view.t1 - view.t0;
      if (view.t0 < 0) { view.t0 = 0; view.t1 = span; }
      if (view.t1 > full) { view.t1 = full; view.t0 = Math.max(0, full - span); }
    }
    // keep the playhead in view while playing (the view scrolls, not the page)
    function follow(t) {
      if (!d) return;
      var span = view.t1 - view.t0;
      if (span >= d.duration - 0.01) return;
      if (t > view.t1 - span * 0.1 || t < view.t0) { view.t0 = t - span * 0.2; view.t1 = view.t0 + span; clampView(); if (opts.onView) opts.onView(view.t0, view.t1, d.duration); }
    }

    function select(k) { sel = k; redraw(); if (opts.onSelect) opts.onSelect(k); }
    function setEditing(on) { editing = !!on; canvas.classList.toggle('editing', editing); }

    function noteAt(x, y, w, h) {
      if (!d || !d.notes) return -1;
      var t = xt(x, w), m = ym(y, h);
      var rowH = (h - AXIS) / (yHi - yLo);
      for (var k = 0; k < d.notes.length; k++) {
        var nt = d.notes[k];
        var a = nt.start * d.hop, b = (nt.end + 1) * d.hop;
        if (t < a || t > b) continue;
        var tm = shownTarget(k);
        if (Math.abs(m - tm) * rowH <= Math.max(9, rowH * 0.6)) return k;
      }
      return -1;
    }
    function shownTarget(k) {
      var ov = d.overrides || {};
      var nt = d.notes[k];
      if (Object.prototype.hasOwnProperty.call(ov, k) && ov[k] === null) return nt.center;
      var base = Object.prototype.hasOwnProperty.call(ov, k) ? ov[k] : nt.target;
      return base + (d.land || 0);
    }

    function redraw() {
      var sz = size(), w = sz.w, h = sz.h;
      var ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var bg = css(canvas, '--lg-surface-2', '#1b2130'), text = css(canvas, '--lg-text', '#e8ecf4');
      var text3 = css(canvas, '--lg-text-3', '#8a93a6'), text2 = css(canvas, '--lg-text-2', '#b8c0d0');
      var accent = css(canvas, '--lg-accent', '#7aa7ff'), warn = css(canvas, '--lg-warn', '#f59e0b');
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);
      if (!d) return;
      var plotH = h - AXIS;
      var rowH = plotH / (yHi - yLo);
      var shift = d.rowShift || 0;
      // semitone rows
      ctx.font = '12px system-ui, sans-serif';
      ctx.textBaseline = 'middle';
      for (var k = Math.floor(yLo - shift) - 1; k <= Math.ceil(yHi - shift) + 1; k++) {
        var yc = my(k + shift, h);
        var y0 = yc - rowH / 2;
        var pc = ((k % 12) + 12) % 12;
        var inKey = d.pcs ? d.pcs[pc] : true;
        if (inKey) {
          ctx.fillStyle = alpha(text, 0.05);
          ctx.fillRect(GUTTER, y0, w - GUTTER, rowH);
        }
        ctx.strokeStyle = alpha(text, pc === 0 ? 0.18 : 0.06);
        ctx.beginPath(); ctx.moveTo(GUTTER, y0 + 0.5); ctx.lineTo(w, y0 + 0.5); ctx.stroke();
        if (rowH >= 13 || pc === 0) {
          ctx.fillStyle = inKey ? text2 : text3;
          ctx.fillText(opts.noteName ? opts.noteName(k) : String(k), 6, yc);
        }
      }
      // notes
      var ov = d.overrides || {};
      for (var n = 0; n < (d.notes || []).length; n++) {
        var nt = d.notes[n];
        var xa = tx(nt.start * d.hop, w), xb = tx((nt.end + 1) * d.hop, w);
        if (xb < GUTTER || xa > w) continue;
        var skipped = Object.prototype.hasOwnProperty.call(ov, n) && ov[n] === null;
        var moved = Object.prototype.hasOwnProperty.call(ov, n) && ov[n] !== null;
        var tm = shownTarget(n);
        if (drag && drag.mode === 'note' && drag.note === n) tm += drag.delta;
        var yy = my(tm, h), hh = Math.max(4, rowH * 0.62);
        ctx.fillStyle = skipped ? alpha(text3, 0.12) : (moved ? alpha(warn, 0.26) : alpha(accent, 0.22));
        ctx.strokeStyle = skipped ? alpha(text3, 0.7) : (moved ? warn : accent);
        ctx.lineWidth = n === sel ? 2.5 : 1;
        if (skipped) ctx.setLineDash([4, 3]);
        roundRect(ctx, Math.max(GUTTER, xa), yy - hh / 2, Math.max(2, Math.min(w, xb) - Math.max(GUTTER, xa)), hh, Math.min(4, hh / 2));
        ctx.fill(); ctx.stroke();
        ctx.setLineDash([]);
      }
      // curves
      curve(ctx, d.midi, alpha(text3, 0.8), 1.3, w, h);
      if (d.out) curve(ctx, d.out, accent, 2, w, h);
      // time axis
      ctx.fillStyle = alpha(text, 0.06);
      ctx.fillRect(GUTTER, plotH, w - GUTTER, AXIS);
      ctx.fillStyle = text3;
      ctx.textBaseline = 'middle';
      var span = view.t1 - view.t0;
      var step = span > 120 ? 30 : span > 60 ? 10 : span > 20 ? 5 : span > 8 ? 2 : span > 3 ? 1 : 0.5;
      for (var t = Math.ceil(view.t0 / step) * step; t <= view.t1; t += step) {
        var xx = tx(t, w);
        ctx.fillRect(xx, plotH, 1, 4);
        ctx.fillText(fmt(t), xx + 3, plotH + AXIS / 2 + 1);
      }
      // playhead
      if (playhead != null && playhead >= view.t0 && playhead <= view.t1) {
        var px = tx(playhead, w);
        ctx.fillStyle = '#ff5d73';
        ctx.fillRect(px - 1, 0, 2, plotH);
      }
      // gutter edge
      ctx.fillStyle = alpha(text, 0.12);
      ctx.fillRect(GUTTER - 1, 0, 1, plotH);
    }

    function curve(ctx, arr, color, width, w, h) {
      var hop = d.hop;
      var i0 = Math.max(0, Math.floor(view.t0 / hop) - 1), i1 = Math.min(arr.length - 1, Math.ceil(view.t1 / hop) + 1);
      // at most ~2 points per pixel
      var stride = Math.max(1, Math.floor((i1 - i0) / ((w - GUTTER) * 2)));
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineJoin = 'round';
      ctx.beginPath();
      var pen = false, last = NaN;
      for (var i = i0; i <= i1; i += stride) {
        var v = arr[i];
        if (isNaN(v)) { pen = false; continue; }
        var x = tx((i + 0.5) * hop, w), y = my(v, h);
        // a jump of more than three semitones between two frames is a break
        // in the voice (a rap syllable to the next, a slip), not a line: drawn
        // joined it streaked the whole height of the graph
        if (!pen || Math.abs(v - last) > 3) { ctx.moveTo(x, y); pen = true; } else ctx.lineTo(x, y);
        last = v;
      }
      ctx.stroke();
    }

    function roundRect(ctx, x, y, w, h, r) {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }
    function fmt(t) {
      var m = Math.floor(t / 60), s = t - m * 60;
      return m + ':' + (s < 10 ? '0' : '') + (Math.abs(s - Math.round(s)) < 1e-6 ? Math.round(s) : s.toFixed(1));
    }

    // ------------------------------------------------------------------
    // gestures
    // ------------------------------------------------------------------
    function local(ev) {
      var r = canvas.getBoundingClientRect();
      return { x: ev.clientX - r.left, y: ev.clientY - r.top, w: r.width, h: r.height };
    }
    canvas.addEventListener('pointerdown', function (ev) {
      if (!d || ev.button > 0) return;
      var p = local(ev);
      drag = { id: ev.pointerId, x0: p.x, y0: p.y, t0: view.t0, mode: null, note: noteAt(p.x, p.y, p.w, p.h), delta: 0, touch: ev.pointerType === 'touch', moved: false };
    });
    canvas.addEventListener('pointermove', function (ev) {
      if (!drag || ev.pointerId !== drag.id) return;
      var p = local(ev);
      var dx = p.x - drag.x0, dy = p.y - drag.y0;
      if (!drag.mode) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        drag.moved = true;
        if (drag.note >= 0 && (!drag.touch || editing) && Math.abs(dy) > Math.abs(dx)) drag.mode = 'note';
        else if (Math.abs(dx) >= Math.abs(dy)) drag.mode = 'pan';
        else if (editing) drag.mode = 'pan';  // editing: the canvas owns the finger
        else { drag = null; return; }       // a vertical touch move is the page's scroll
        try { canvas.setPointerCapture(ev.pointerId); } catch (e) { /* */ }
      }
      if (drag.mode === 'note') {
        var rowH = (p.h - AXIS) / (yHi - yLo);
        drag.delta = Math.round(-dy / rowH);
        redraw();
      } else if (drag.mode === 'pan') {
        var span = view.t1 - view.t0;
        var dt = -dx / (p.w - GUTTER) * span;
        view.t0 = drag.t0 + dt; view.t1 = view.t0 + span;
        clampView();
        redraw();
        if (opts.onView) opts.onView(view.t0, view.t1, d.duration);
      }
      if (ev.cancelable) ev.preventDefault();
    });
    function end(ev, cancelled) {
      if (!drag || ev.pointerId !== drag.id) return;
      var dr = drag;
      drag = null;
      if (dr.mode === 'note' && !cancelled && dr.delta && opts.onMove) opts.onMove(dr.note, dr.delta);
      else redraw();
      canvas.__dragged = dr.moved;   // the click that follows a drag is not a tap
    }
    canvas.addEventListener('pointerup', function (ev) { end(ev, false); });
    canvas.addEventListener('pointercancel', function (ev) { end(ev, true); });
    canvas.addEventListener('click', function (ev) {
      if (!d) return;
      if (canvas.__dragged) { canvas.__dragged = false; return; }
      var p = local(ev);
      var k = noteAt(p.x, p.y, p.w, p.h);
      if (k >= 0) select(k);
      else {
        if (sel >= 0) select(-1);
        if (p.x >= GUTTER && opts.onSeek) opts.onSeek(Math.max(0, Math.min(d.duration, xt(p.x, p.w))));
      }
    });
    canvas.addEventListener('wheel', function (ev) {
      if (!d || !(ev.ctrlKey || ev.metaKey)) return;     // plain wheel scrolls the page
      ev.preventDefault();
      var p = local(ev);
      zoom(ev.deltaY < 0 ? 1.25 : 0.8, xt(p.x, p.w));
    }, { passive: false });
    canvas.addEventListener('keydown', function (ev) {
      if (!d || !d.notes || !d.notes.length) return;
      var k = sel;
      if (ev.key === 'ArrowRight' || ev.key === 'ArrowLeft') {
        k = k < 0 ? 0 : Math.max(0, Math.min(d.notes.length - 1, k + (ev.key === 'ArrowRight' ? 1 : -1)));
        select(k);
        var nt = d.notes[k];
        var t = nt.start * d.hop;
        if (t < view.t0 || t > view.t1) setViewStart(t - (view.t1 - view.t0) * 0.2);
        ev.preventDefault();
      } else if ((ev.key === 'ArrowUp' || ev.key === 'ArrowDown') && k >= 0) {
        if (opts.onMove) opts.onMove(k, ev.key === 'ArrowUp' ? 1 : -1);
        ev.preventDefault();
      } else if ((ev.key === 'Delete' || ev.key === 'Backspace') && k >= 0) {
        if (opts.onSkip) opts.onSkip(k);
        ev.preventDefault();
      } else if (ev.key === 'Escape' && k >= 0) {
        select(-1);
      }
    });
    var ro = typeof ResizeObserver === 'function' ? new ResizeObserver(function () { redraw(); }) : null;
    if (ro) ro.observe(canvas);

    return {
      setData: setData, setCurve: setCurve, setNotes: setNotes, setRows: setRows,
      setPlayhead: setPlayhead, follow: follow, zoom: zoom, setViewStart: setViewStart,
      select: select, redraw: redraw, setEditing: setEditing,
      get selected() { return sel; },
      get view() { return { t0: view.t0, t1: view.t1 }; },
      destroy: function () { if (ro) ro.disconnect(); }
    };
  }

  root.AutotuneGraph = { create: create };
})(typeof self !== 'undefined' ? self : this);
